import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useListResultCount } from '../components/Layout';
import { DashboardOverview } from '../components/DashboardOverview';
import { ReferralFiltersBar } from '../components/ReferralFiltersBar';
import { ReferralsTable } from '../components/ReferralsTable';
import { readStoredListCreatedBy, writeStoredListCreatedBy, } from '../lib/createdByScopeStorage';
import { compareChildIncidentLevel } from '../lib/incidentLevels';
import { isOverviewPrivileged, isOverviewProcessor, locksOpenL2 } from '../lib/portalRoles';
import { fetchReferrals } from '../services/referrals';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { DEFAULT_REFERRAL_FILTERS } from '../types/referral';
const PAGE_SIZE = 10;
/** Columns the BFF can sort across the loaded dataset. All others sort
 * the currently-loaded page client-side. */
const SERVER_SORT_COLUMNS = ['created', 'updated'];
const STRING_COLUMNS = ['issueType', 'createdByName', 'ownerName'];
function compareReferrals(a, b, column) {
    if (STRING_COLUMNS.includes(column)) {
        return String(a[column] ?? '').localeCompare(String(b[column] ?? ''), undefined, {
            sensitivity: 'base',
        });
    }
    return Number(a[column] ?? 0) - Number(b[column] ?? 0);
}
/** Build a windowed list of page numbers with ellipsis gaps (null = gap). */
function buildPageList(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2)
        pages.push(null);
    for (let p = start; p <= end; p += 1)
        pages.push(p);
    if (end < total - 1)
        pages.push(null);
    pages.push(total);
    return pages;
}
export function DashboardPage() {
    const [referrals, setReferrals] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortColumn, setSortColumn] = useState('created');
    const [sortDirection, setSortDirection] = useState('desc');
    const [pageInput, setPageInput] = useState('');
    const [filters, setFilters] = useState(DEFAULT_REFERRAL_FILTERS);
    const [filtersReady, setFiltersReady] = useState(false);
    const [viewerId, setViewerId] = useState(null);
    const [processor, setProcessor] = useState(false);
    const [privileged, setPrivileged] = useState(false);
    const [lockOpenL2, setLockOpenL2] = useState(false);
    const { setListResultCount } = useListResultCount();
    const dateSortColumn = sortColumn === 'updated' ? 'updated' : 'created';
    const dateSortOrder = SERVER_SORT_COLUMNS.includes(sortColumn) && sortDirection === 'asc' ? 'ASC' : 'DESC';
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            const detail = info.detail;
            const id = typeof detail?.id === 'number' ? detail.id : null;
            const roleName = detail?.roleName?.trim() ||
                (typeof detail?.role?.name === 'string' ? detail.role.name.trim() : '') ||
                '';
            const isProcessor = isOverviewProcessor(roleName);
            const isPrivileged = isOverviewPrivileged(roleName);
            setViewerId(id);
            setProcessor(isProcessor);
            setPrivileged(isPrivileged);
            setLockOpenL2(locksOpenL2(roleName));
            let createdBy = null;
            if (id != null && (isProcessor || isPrivileged)) {
                const stored = readStoredListCreatedBy(id, isProcessor ? 'processor' : 'privileged');
                if (stored) {
                    createdBy = stored.createdBy;
                }
                else if (isProcessor) {
                    createdBy = id;
                }
            }
            setFilters((prev) => ({ ...prev, createdBy }));
            setFiltersReady(true);
        })
            .catch((err) => {
            console.error('[dashboard] signin user failed', err);
            if (!cancelled)
                setFiltersReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        setListResultCount(totalCount, loading || !filtersReady);
    }, [totalCount, loading, filtersReady, setListResultCount]);
    useEffect(() => {
        return () => setListResultCount(null);
    }, [setListResultCount]);
    const loadReferrals = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const page = await fetchReferrals(pageIndex, PAGE_SIZE, dateSortOrder, filters, dateSortColumn);
            setReferrals(page.datas);
            setTotalCount(page.totalCount);
        }
        catch (err) {
            setReferrals([]);
            setTotalCount(0);
            setError(err instanceof Error ? err.message : 'Failed to load L1s.');
        }
        finally {
            setLoading(false);
        }
    }, [pageIndex, dateSortOrder, dateSortColumn, filters]);
    useEffect(() => {
        if (!filtersReady)
            return;
        void loadReferrals();
    }, [filtersReady, loadReferrals]);
    useEffect(() => {
        if (!filtersReady || viewerId == null || (!processor && !privileged))
            return;
        writeStoredListCreatedBy(viewerId, processor ? 'processor' : 'privileged', filters.createdBy);
    }, [filtersReady, viewerId, processor, privileged, filters.createdBy]);
    const defaultFilters = useMemo(() => {
        if (processor && viewerId != null) {
            return { ...DEFAULT_REFERRAL_FILTERS, createdBy: viewerId };
        }
        return DEFAULT_REFERRAL_FILTERS;
    }, [processor, viewerId]);
    const handleFiltersChange = useCallback((next) => {
        setFilters(next);
        setPageIndex(1);
    }, []);
    const handleClearFilters = useCallback(() => {
        setFilters(defaultFilters);
        setPageIndex(1);
    }, [defaultFilters]);
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const rangeStart = totalCount === 0 ? 0 : (pageIndex - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(pageIndex * PAGE_SIZE, totalCount);
    const sortedReferrals = useMemo(() => {
        const parents = referrals.filter((row) => row.parentId == null);
        const childrenByParent = new Map();
        for (const row of referrals) {
            if (row.parentId == null)
                continue;
            const list = childrenByParent.get(row.parentId) ?? [];
            list.push(row);
            childrenByParent.set(row.parentId, list);
        }
        if (!SERVER_SORT_COLUMNS.includes(sortColumn)) {
            parents.sort((a, b) => compareReferrals(a, b, sortColumn));
            if (sortDirection === 'desc')
                parents.reverse();
        }
        const ordered = [];
        for (const parent of parents) {
            ordered.push(parent);
            const kids = (childrenByParent.get(parent.id) ?? []).sort((a, b) => compareChildIncidentLevel(a.level, b.level) || a.id - b.id);
            ordered.push(...kids);
        }
        const orphanParents = [...childrenByParent.keys()].filter((id) => !parents.some((parent) => parent.id === id));
        for (const parentId of orphanParents) {
            const kids = (childrenByParent.get(parentId) ?? []).sort((a, b) => compareChildIncidentLevel(a.level, b.level) || a.id - b.id);
            ordered.push(...kids);
        }
        return ordered;
    }, [referrals, sortColumn, sortDirection]);
    const handleSort = useCallback((column) => {
        const nextDirection = sortColumn === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortColumn(column);
        setSortDirection(nextDirection);
        // Server-side (date) sorting re-orders the whole dataset, so reset to page 1.
        if (SERVER_SORT_COLUMNS.includes(column)) {
            setPageIndex(1);
        }
    }, [sortColumn, sortDirection]);
    const goToPage = useCallback((page) => {
        const clamped = Math.min(Math.max(1, page), totalPages);
        setPageIndex(clamped);
    }, [totalPages]);
    const handleJump = useCallback((e) => {
        e.preventDefault();
        const parsed = Number(pageInput);
        if (Number.isFinite(parsed) && parsed >= 1) {
            goToPage(Math.floor(parsed));
            setPageInput('');
        }
    }, [pageInput, goToPage]);
    const pageList = useMemo(() => buildPageList(pageIndex, totalPages), [pageIndex, totalPages]);
    return (_jsxs("div", { className: "page", children: [error && _jsx("div", { className: "table-empty", children: error }), _jsx(DashboardOverview, {}), _jsx(ReferralFiltersBar, { filters: filters, onChange: handleFiltersChange, onClear: handleClearFilters }), _jsx(ReferralsTable, { referrals: sortedReferrals, loading: loading || !filtersReady, sortColumn: sortColumn, sortDirection: sortDirection, onSort: handleSort, lockOpenL2: lockOpenL2 }), _jsxs("div", { className: "pagination", children: [_jsx("span", { className: "pagination-info", children: totalCount === 0
                            ? 'No results'
                            : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}` }), _jsxs("div", { className: "pagination-controls", children: [_jsx("button", { type: "button", className: "btn btn-ghost", disabled: loading || !filtersReady || pageIndex <= 1, onClick: () => goToPage(pageIndex - 1), children: "Previous" }), _jsx("div", { className: "pagination-pages", children: pageList.map((page, idx) => page === null ? (_jsx("span", { className: "pagination-gap", children: "\u2026" }, `gap-${idx}`)) : (_jsx("button", { type: "button", className: `pagination-page-btn${page === pageIndex ? ' active' : ''}`, disabled: loading || !filtersReady, onClick: () => goToPage(page), children: page }, page))) }), _jsx("button", { type: "button", className: "btn btn-ghost", disabled: loading || !filtersReady || pageIndex >= totalPages, onClick: () => goToPage(pageIndex + 1), children: "Next" }), totalPages > 1 && (_jsxs("form", { className: "pagination-jump", onSubmit: handleJump, children: [_jsx("label", { htmlFor: "page-jump", children: "Go to" }), _jsx("input", { id: "page-jump", type: "number", min: 1, max: totalPages, value: pageInput, placeholder: String(pageIndex), onChange: (e) => setPageInput(e.target.value) }), _jsx("button", { type: "submit", className: "btn btn-ghost", disabled: loading || !filtersReady || !pageInput, children: "Go" })] }))] })] })] }));
}
